/**
 * Pokemon Variant Display Component
 * แสดงผล Pokemon Variants อย่างสมบูรณ์และสวยงาม
 */

import React from 'react';
import { motion } from 'framer-motion';
import { getPokemonImage, createPokemonImageErrorHandler } from '../utils/pokemonImageResolver';

const PokemonVariantDisplay = ({
  pokemon,
  className = '',
  showBadges = true,
  showStats = false,
  size = 'medium',
  onClick = null
}) => {
  // กำหนดขนาด
  const sizeClasses = {
    small: 'w-16 h-16',
    medium: 'w-24 h-24',
    large: 'w-32 h-32',
    xl: 'w-40 h-40'
  };

  // กำหนดขนาด Badge
  const badgeSizeClasses = {
    small: 'text-xs px-1 py-0.5',
    medium: 'text-sm px-2 py-1',
    large: 'text-base px-3 py-1.5',
    xl: 'text-lg px-4 py-2'
  };

  // กำหนดขนาด Badge Icon
  const badgeIconSizeClasses = {
    small: 'w-3 h-3',
    medium: 'w-4 h-4',
    large: 'w-5 h-5',
    xl: 'w-6 h-6'
  };

  // ตรวจสอบว่าเป็น Variant หรือไม่
  const isVariant = pokemon?.is_variant || false;
  const isShadow = pokemon?.is_shadow || false;
  const isRegional = pokemon?.is_regional || false;
  const isWeather = pokemon?.is_weather || false;
  const isSpecial = pokemon?.is_special || false;

  // ข้อมูล Variants
  const variants = pokemon?.variants || [];
  const variantTypes = pokemon?.variant_types || [];
  const cssClasses = pokemon?.css_classes || [];
  const badges = pokemon?.badges || [];

  // สร้าง CSS Classes
  const getVariantClasses = () => {
    const classes = ['pokemon-variant-card', sizeClasses[size]];
    
    if (isVariant) {
      classes.push('variant-pokemon');
    }
    
    if (isShadow) {
      classes.push('shadow-pokemon');
    }
    
    if (isRegional) {
      classes.push('regional-pokemon');
    }
    
    if (isWeather) {
      classes.push('weather-pokemon');
    }
    
    if (isSpecial) {
      classes.push('special-pokemon');
    }
    
    // เพิ่ม CSS Classes จากฐานข้อมูล
    if (cssClasses.length > 0) {
      classes.push(...cssClasses);
    }
    
    return classes.join(' ');
  };

  // สร้าง Badge Style
  const getBadgeStyle = (badge) => {
    const baseStyle = 'absolute rounded-full font-bold text-white shadow-lg border border-white/20';
    const sizeStyle = badgeSizeClasses[size];
    
    let typeStyle = '';
    switch (badge.type) {
      case 'shadow':
        typeStyle = 'bg-gradient-to-r from-purple-900 to-purple-700';
        break;
      case 'regional':
        typeStyle = 'bg-gradient-to-r from-blue-600 to-blue-800';
        break;
      case 'weather':
        typeStyle = 'bg-gradient-to-r from-yellow-500 to-orange-600';
        break;
      case 'special':
        typeStyle = 'bg-gradient-to-r from-pink-600 to-purple-600';
        break;
      default:
        typeStyle = 'bg-gradient-to-r from-gray-600 to-gray-800';
    }
    
    return `${baseStyle} ${sizeStyle} ${typeStyle}`;
  };

  // สร้าง Badge Icon
  const getBadgeIcon = (badge) => {
    const iconSize = badgeIconSizeClasses[size];
    
    switch (badge.type) {
      case 'shadow':
        return (
          <svg className={iconSize} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l2-2a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'regional':
        return (
          <svg className={iconSize} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
        );
      case 'weather':
        return (
          <svg className={iconSize} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.05A7 7 0 0110 2a7 7 0 014.95 4.95l-.707.707A5.5 5.5 0 0010 3.5a5.5 5.5 0 00-4.243 2.95l-.707-.707z" clipRule="evenodd" />
          </svg>
        );
      case 'special':
        return (
          <svg className={iconSize} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className={iconSize} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  // สร้าง Background Image - ปิดการใช้งานพื้นหลังรูปบอล
  const getBackgroundImage = () => {
    return null; // ไม่แสดงพื้นหลังรูปบอล
  };

  // สร้าง Badge Position
  const getBadgePosition = (position) => {
    switch (position) {
      case 'top-left':
        return 'top-1 left-1';
      case 'top-right':
        return 'top-1 right-1';
      case 'bottom-left':
        return 'bottom-1 left-1';
      case 'bottom-right':
        return 'bottom-1 right-1';
      default:
        return 'top-1 right-1';
    }
  };

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    hover: { 
      scale: 1.05,
      transition: {
        duration: 0.2,
        ease: "easeInOut"
      }
    },
    tap: { 
      scale: 0.95,
      transition: {
        duration: 0.1,
        ease: "easeInOut"
      }
    }
  };

  const badgeVariants = {
    hidden: { opacity: 0, scale: 0 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        duration: 0.2,
        delay: 0.1,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div
      className={`relative ${getVariantClasses()} ${className}`}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      whileTap="tap"
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {/* Background Image - ปิดการใช้งานพื้นหลังรูปบอล */}
      {getBackgroundImage() && (
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20 z-0"
          style={{
            backgroundImage: `url(${getBackgroundImage()})`
          }}
        />
      )}
      
      {/* Pokemon Image */}
      <div className="relative z-10 flex items-center justify-center h-full">
        {(() => {
          // Use pokemonImageResolver - SINGLE SOURCE OF TRUTH
          const primaryImage = getPokemonImage(pokemon, 'default');
          const handleImageError = createPokemonImageErrorHandler(pokemon, { variant: 'normal' });
          
          return (
            <img
              src={primaryImage}
              alt={pokemon?.pokemon_name || pokemon?.name || pokemon?.name_th || 'Unknown Pokemon'}
              className="w-full h-full object-contain"
              onError={handleImageError}
            />
          );
        })()}
      </div>
      
      {/* Shadow Effect - Purple Smoke Background */}
      {isShadow && (
        <>
          {/* Purple Smoke Background */}
          <div 
            className="absolute inset-0 z-0 opacity-40"
            style={{
              background: `radial-gradient(ellipse at center, 
                rgba(147, 51, 234, 0.4) 0%, 
                rgba(124, 58, 237, 0.3) 20%,
                rgba(139, 92, 246, 0.25) 40%,
                rgba(168, 85, 247, 0.2) 60%,
                rgba(196, 181, 253, 0.15) 80%,
                transparent 100%)`,
              backgroundBlendMode: 'multiply'
            }}
          />
          {/* Additional Purple Aura Effect */}
          <div 
            className="absolute inset-0 z-0"
            style={{
              background: `radial-gradient(circle at 50% 70%, 
                rgba(147, 51, 234, 0.3) 0%, 
                rgba(124, 58, 237, 0.2) 30%,
                rgba(139, 92, 246, 0.1) 50%,
                transparent 70%)`,
              filter: 'blur(8px)',
              transform: 'scale(1.2)'
            }}
          />
        </>
      )}
      
      {/* Badges */}
      {showBadges && badges.length > 0 && (
        <div className="absolute inset-0 z-30">
          {badges.map((badge, index) => (
            <motion.div
              key={index}
              className={`${getBadgeStyle(badge)} ${getBadgePosition(badge.position || 'top-right')}`}
              variants={badgeVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: index * 0.1 }}
            >
              <div className="flex items-center gap-1">
                {getBadgeIcon(badge)}
                <span className="font-bold">{badge.name}</span>
              </div>
            </motion.div>
          ))}
        </div>
      )}
      
      {/* Stats Overlay */}
      {showStats && pokemon && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/80 text-white text-xs p-1 z-40">
          <div className="flex justify-between">
            <span>#{pokemon.rank_position}</span>
            <span>{pokemon.score}</span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default PokemonVariantDisplay;